import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import {
  locationsGetById,
  locationsUpdateVoidAndRefundSettings,
} from "~/api-refs";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { convexClient } from "~/integrations/convex/provider";
import { ProductEvents } from "~/lib/posthog";

import { Checkbox } from "../ui/checkbox";
import { Switch } from "../ui/switch";

export function VoidAndRefundsPage({
  locationId,
}: {
  locationId: Id<"locations">;
}) {
  const [requirePinVoidingItems, setRequirePinVoidingItems] = useState(false);
  const [requireReasonVoidingItems, setRequireReasonVoidingItems] =
    useState(false);
  const [allowPartialRefunds, setAllowPartialRefunds] = useState(false);
  const [allowPartialRefundsByItem, setAllowPartialRefundsByItem] =
    useState(false);
  const [allowPartialRefundsByAmount, setAllowPartialRefundsByAmount] =
    useState(false);
  const [allowCashRefunds, setAllowCashRefunds] = useState(false);

  const { data: location, isPending } = useQuery(
    convexQuery(locationsGetById, {
      id: locationId,
    })
  );

  const updateVoidAndRefundSettingsMutation = useMutation({
    mutationFn: (data: {
      locationId: Id<"locations">;
      requirePinVoidingItems?: boolean;
      requireReasonVoidingItems?: boolean;
      allowPartialRefunds?: boolean;
      allowPartialRefundsByItem?: boolean;
      allowPartialRefundsByAmount?: boolean;
      allowCashRefunds?: boolean;
    }) => convexClient.mutation(locationsUpdateVoidAndRefundSettings, data),
    onSuccess: () => {
      ProductEvents.settingsUpdated("voids-and-refunds");
    },
  });

  useEffect(() => {
    if (location) {
      setRequirePinVoidingItems(location?.requirePinVoidingItems ?? false);
      setRequireReasonVoidingItems(
        location?.requireReasonVoidingItems ?? false
      );
      setAllowPartialRefunds(location?.allowPartialRefunds ?? false);
      setAllowCashRefunds(location?.allowCashRefunds ?? false);
      setAllowPartialRefundsByItem(
        location?.allowPartialRefundsByItem ?? false
      );
      setAllowPartialRefundsByAmount(
        location?.allowPartialRefundsByAmount ?? false
      );
    }
  }, [location]);

  if (isPending) {
    return <LoadingPanel message="Loading settings..." />;
  }

  return (
    <div className="float-start flex flex-col gap-6">
      <div id="voiding-items">
        <div className="flex flex-col justify-between gap-1 py-5">
          <h2 className="text-foreground text-weight-medium">Voiding Items</h2>
          <p className="body-sm text-muted-foreground leading-tight">
            Configure void settings for this location
          </p>
        </div>

        <div className="flex flex-col justify-between gap-2">
          <div className="mt-2 flex items-center gap-2">
            <Switch
              checked={requirePinVoidingItems}
              id="require-pin-voiding-items"
              onCheckedChange={(checked: boolean) => {
                setRequirePinVoidingItems(checked);
                if (location?._id) {
                  updateVoidAndRefundSettingsMutation.mutate({
                    locationId: location?._id,
                    requirePinVoidingItems: checked,
                  });
                }
              }}
            />
            <label htmlFor="require-pin-voiding-items">
              <p className="body-sm text-foreground">
                Require PIN authorization to void items sent to kitchen
              </p>
            </label>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Switch
              checked={requireReasonVoidingItems}
              id="require-reason-voiding-items"
              onCheckedChange={(checked: boolean) => {
                setRequireReasonVoidingItems(checked);
                if (location?._id) {
                  updateVoidAndRefundSettingsMutation.mutate({
                    locationId: location?._id,
                    requireReasonVoidingItems: checked,
                  });
                }
              }}
            />
            <label htmlFor="require-reason-voiding-items">
              <p className="body-sm text-foreground">
                Require reason for voiding items sent to kitchen
              </p>
            </label>
          </div>
        </div>
      </div>
      <div id="refunds">
        <div className="flex flex-col justify-between gap-1 py-5">
          <h2 className="text-foreground text-weight-medium">Refunds</h2>
          <p className="body-sm text-muted-foreground leading-tight">
            Configure refund settings for this location
          </p>
        </div>
        <div className="flex flex-col justify-between gap-2">
          <div>
            <div className="mt-2 flex items-center gap-2">
              <Switch
                checked={allowPartialRefunds}
                id="allow-partial-refunds"
                onCheckedChange={(checked: boolean) => {
                  setAllowPartialRefunds(checked);
                  if (location?._id) {
                    if (!checked) {
                      setAllowPartialRefundsByItem(false);
                      setAllowPartialRefundsByAmount(false);

                      updateVoidAndRefundSettingsMutation.mutate({
                        locationId: location?._id,
                        allowPartialRefunds: false,
                        allowPartialRefundsByItem: false,
                        allowPartialRefundsByAmount: false,
                      });
                      return;
                    }

                    updateVoidAndRefundSettingsMutation.mutate({
                      locationId: location?._id,
                      allowPartialRefunds: checked,
                    });
                  }
                }}
              />
              <label htmlFor="allow-partial-refunds">
                <p className="body-sm text-foreground">
                  Allow partial refunds on orders
                </p>
              </label>
            </div>
            {allowPartialRefunds && (
              <div className="flex flex-col justify-between pl-12">
                <div className="mt-2 flex items-center gap-2">
                  <Checkbox
                    checked={allowPartialRefundsByItem}
                    id="allow-partial-refunds-by-item"
                    onCheckedChange={(checked: boolean) => {
                      setAllowPartialRefundsByItem(checked);
                      if (location?._id) {
                        updateVoidAndRefundSettingsMutation.mutate({
                          locationId: location?._id,
                          allowPartialRefundsByItem: checked,
                        });
                      }
                    }}
                  />
                  <label htmlFor="allow-partial-refunds-by-item">
                    <p className="body-sm text-foreground">
                      Allow partial refunds by item
                    </p>
                  </label>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <Checkbox
                    checked={allowPartialRefundsByAmount}
                    id="allow-partial-refunds-by-amount"
                    onCheckedChange={(checked: boolean) => {
                      setAllowPartialRefundsByAmount(checked);
                      if (location?._id) {
                        updateVoidAndRefundSettingsMutation.mutate({
                          locationId: location?._id,
                          allowPartialRefundsByAmount: checked,
                        });
                      }
                    }}
                  />
                  <label htmlFor="allow-partial-refunds-by-amount">
                    <p className="body-sm text-foreground">
                      Allow partial refunds by amount
                    </p>
                  </label>
                </div>
              </div>
            )}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Switch
              checked={allowCashRefunds}
              id="allow-cash-refunds"
              onCheckedChange={(checked: boolean) => {
                setAllowCashRefunds(checked);
                if (location?._id) {
                  updateVoidAndRefundSettingsMutation.mutate({
                    locationId: location?._id,
                    allowCashRefunds: checked,
                  });
                }
              }}
            />
            <label htmlFor="allow-cash-refunds">
              <p className="body-sm text-foreground">
                Allow cash refunds on credit/debit transactions
              </p>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
